import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { setNotification } from '../../redux/notificationSlice'
import { useLeadFunnelBlocksPermissions } from './lead_funnel_blocks/useLeadFunnelBlocksPermissions'
import { useBlocksExplorerData } from './lead_funnel_blocks/useBlocksExplorerData'
import { useBlockChangeRequestApi } from './lead_funnel_blocks/useBlockChangeRequestApi'
import {
  cardsForBlock,
  clearRowFormDrafts,
  diffCardBlock,
  operationTarget,
  rowActions,
  statusMeta,
} from './lead_funnel_blocks/blockChangeHelpers'
import {
  buildUrlState,
  chipCounts,
  filterBlocks,
  paginate,
  readUrlState,
  sortBlocks,
  treatmentPath,
} from './lead_funnel_blocks/blocksExplorerHelpers'
import LayoutSidebar from '../../components/LayoutSidebar'
import Loading from '../../components/Loading'
import TreatmentTree from './lead_funnel_blocks/TreatmentTree'
import BlocksToolbar from './lead_funnel_blocks/BlocksToolbar'
import StatsStrip from './lead_funnel_blocks/StatsStrip'
import ParityBadge from './lead_funnel_blocks/ParityBadge'
import BlocksTable, { BlocksPager } from './lead_funnel_blocks/BlocksTable'
import BlockDrawer from './lead_funnel_blocks/BlockDrawer'
import BlockChangeActions from './lead_funnel_blocks/BlockChangeActions'
import BlocksViewModeBar from './lead_funnel_blocks/BlocksViewModeBar'
import BlocksDriftBanner from './lead_funnel_blocks/BlocksDriftBanner'
import BlocksDraftTray from './lead_funnel_blocks/BlocksDraftTray'
import BlocksNewBlockPanel from './lead_funnel_blocks/BlocksNewBlockPanel'

const EDIT_OPS = ['block.update', 'block.override']

function collectTreatmentNames(nodes, names = {}) {
  nodes.forEach((node) => {
    names[node.id] = node.name
    collectTreatmentNames(node.children, names)
  })
  return names
}

export default function LeadFunnelBlocks() {
  const { permissions, currentUserId, isSpoofing } =
    useLeadFunnelBlocksPermissions()
  const [searchParams, setSearchParams] = useSearchParams()
  // react-router hands a functional update the params of the render that made the setter, so a call finishing
  // after the URL moved must use the latest setter, or it writes the old URL back.
  const latestSetSearchParams = useRef(setSearchParams)
  latestSetSearchParams.current = setSearchParams
  const urlState = readUrlState(searchParams)
  const dispatch = useDispatch()
  const [chip, setChip] = useState('all')
  const [blockTable, setBlockTable] = useState('all')
  const [sort, setSort] = useState({ key: null, direction: 'asc' })
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const selectedBlockKey = urlState.block
    ? `${urlState.block.table}:${urlState.block.id}`
    : null
  // Keyed by block so neither carries over to a newly selected one, not even for the commit before the reset.
  const [editing, setEditing] = useState({ blockKey: null, op: null })
  const [drawerTabState, setDrawerTabState] = useState({
    blockKey: null,
    tab: 'overview',
  })
  const editingOp = editing.blockKey === selectedBlockKey ? editing.op : null
  const setEditingOp = (op) => setEditing({ blockKey: selectedBlockKey, op })
  const drawerTab =
    drawerTabState.blockKey === selectedBlockKey
      ? drawerTabState.tab
      : 'overview'
  const setDrawerTab = (tab) =>
    setDrawerTabState({ blockKey: selectedBlockKey, tab })
  // The keys hide state for another block; this clears it, so returning to a block does not reopen its editor.
  useEffect(() => {
    setEditing({ blockKey: null, op: null })
    setDrawerTabState({ blockKey: null, tab: 'overview' })
  }, [selectedBlockKey])
  const [isCreatingBlock, setIsCreatingBlock] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const api = useBlockChangeRequestApi()

  const {
    treatmentId,
    tree,
    vocabulary,
    explorer,
    isExplorerLoading,
    isExplorerCurrent,
    explorerLoadFailed,
    retryExplorer,
    treeLoadFailed,
    retryTree,
    viewKey,
    parity,
    detail,
    isDetailLoading,
    detailLoadFailed,
    retryDetail,
    checksum,
    draft,
    isDraftClosed,
    vocabularyLoadFailed,
    retryVocabulary,
    myDrafts,
    refresh,
  } = useBlocksExplorerData({
    requestedTreatmentId: urlState.treatmentId,
    customerType: urlState.customerType,
    block: urlState.block,
    changeRequestId: urlState.changeRequestId,
    draftAuthorId: permissions.canCreate ? currentUserId : null,
  })
  const draftRequest = draft?.change_request
  const canEdit =
    Boolean(draftRequest) &&
    draftRequest.id === urlState.changeRequestId &&
    statusMeta(draftRequest.status).isEditable &&
    draftRequest.created_by_user_id === currentUserId &&
    permissions.canCreate &&
    !isSpoofing

  const updateUrl = (patch) =>
    setSearchParams(buildUrlState({ ...urlState, treatmentId, ...patch }), {
      replace: true,
    })

  useEffect(() => {
    setPage(1)
  }, [
    treatmentId,
    urlState.customerType,
    urlState.query,
    chip,
    blockTable,
    urlState.changeRequestId,
  ])

  const treatmentNamesById = useMemo(
    () => (tree ? collectTreatmentNames(tree.nodes) : {}),
    [tree]
  )
  const path = useMemo(
    () => (tree ? treatmentPath(tree.nodes, treatmentId) : []),
    [tree, treatmentId]
  )
  const currentTreatment = path[path.length - 1] ?? null
  const actionLabels = useMemo(
    () =>
      Object.fromEntries(
        (vocabulary?.block_actions ?? []).map((action) => [
          action.value,
          action.label,
        ])
      ),
    [vocabulary]
  )
  const allRows = useMemo(() => {
    if (!isExplorerCurrent) return []
    const blocks = [...explorer.numeric_blocks, ...explorer.categorical_blocks]
    if (blockTable === 'all') return blocks
    return blocks.filter((explorerRow) => explorerRow.table === blockTable)
  }, [explorer, isExplorerCurrent, blockTable])
  const counts = useMemo(
    () => chipCounts(allRows, { query: urlState.query, treatmentId }),
    [allRows, urlState.query, treatmentId]
  )
  const rows = useMemo(() => {
    const filtered = filterBlocks(allRows, {
      query: urlState.query,
      chip,
      treatmentId,
    })
    return sortBlocks(filtered, { ...sort, treatmentNamesById })
  }, [allRows, urlState.query, chip, treatmentId, sort, treatmentNamesById])
  const paged = paginate(rows, { page, perPage })

  // A treatment opens on its first block once its list arrives; a block already in the URL wins.
  const autoSelectedViewKey = useRef(null)
  useEffect(() => {
    if (!isExplorerCurrent || autoSelectedViewKey.current === viewKey) return
    autoSelectedViewKey.current = viewKey
    if (urlState.block || isCreatingBlock || rows.length === 0) return
    const [first] = rows
    updateUrl({ block: { table: first.table, id: first.block.row.id } })
    // Runs once per loaded view; rows and the URL are read at that moment on purpose.
  }, [isExplorerCurrent, viewKey])

  const toggleSort = (key) => {
    setSort((current) => {
      if (current.key !== key) return { key, direction: 'asc' }
      return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    })
  }

  const notify = (type, body) => dispatch(setNotification({ type, body }))
  const copyRule = (rule) => {
    const failed = () => notify('error', 'Could not copy the rule.')
    if (!navigator.clipboard?.writeText) {
      failed()
      return
    }
    navigator.clipboard
      .writeText(rule)
      .then(() => notify('success', 'Rule copied.'))
      .catch(failed)
  }

  // A 409 means the draft left an editable state elsewhere; the add and remove calls re-read it so canEdit follows.
  const withBusy = (promise) => {
    setIsBusy(true)
    return promise
      .then((result) => {
        if (result !== null) refresh()
        return result !== null
      })
      .finally(() => setIsBusy(false))
  }
  const addOperation = ({ op, target, after, onFieldErrors }) =>
    withBusy(
      api.addOperation(
        urlState.changeRequestId,
        { op, ...target, after },
        { onFieldErrors, onConflict: refresh }
      )
    ).then((saved) => {
      if (saved) notify('success', 'Added to the draft.')
      return saved
    })

  const changeCustomerType = (type) => updateUrl({ customerType: type })
  const selectTreatment = (id) => {
    setIsCreatingBlock(false)
    updateUrl({ treatmentId: id, block: null })
  }
  const selectDraft = (changeRequestId) => {
    setIsCreatingBlock(false)
    updateUrl({ changeRequestId, block: null })
  }
  const removeOperation = (sequence) => {
    // Removing the change that made the open draft row deletes that row, so close its drawer once it is gone.
    const card = draft?.diff_cards?.find((item) => item.sequence === sequence)
    const madeRow = card?.temp_ref ? diffCardBlock(card) : null
    const madeBlockKey = madeRow ? `${madeRow.table}:${madeRow.id}` : null
    const { changeRequestId } = urlState
    withBusy(
      api.removeOperation(
        changeRequestId,
        sequence,
        draft?.change_request?.operations_hash,
        { onConflict: refresh }
      )
    ).then((removed) => {
      if (!removed) return
      if (madeRow) {
        clearRowFormDrafts({ changeRequestId, ...madeRow })
        // The latest setter, so a search or filter changed while the call ran is kept.
        latestSetSearchParams.current(
          (previous) => {
            if (previous.get('block') !== madeBlockKey) return previous
            const next = new URLSearchParams(previous)
            next.delete('block')
            return next
          },
          { replace: true }
        )
      }
      notify('success', 'Removed from the draft.')
    })
  }
  // A change opens on the treatment it lands on, so an override shows as the row it makes there.
  const openDraftChange = (card) => {
    setIsCreatingBlock(false)
    updateUrl({
      treatmentId: card.treatment_id ?? treatmentId,
      block: diffCardBlock(card),
    })
  }
  const submitBlockOperation = ({ op, after, onFieldErrors }) => {
    const { overrideTargetId } = rowActions({ detail })
    const id =
      op === 'block.override' && overrideTargetId !== null
        ? overrideTargetId
        : detail.row.id
    const target = operationTarget({ table: detail.table, id })
    return addOperation({ op, target, after, onFieldErrors }).then((saved) => {
      if (saved) setDrawerTab('history')
      return saved
    })
  }
  const createBlock = ({ table, after, onFieldErrors }) => {
    const target = { target_table: table }
    return addOperation({
      op: 'block.create',
      target,
      after,
      onFieldErrors,
    }).then((saved) => {
      if (saved) setIsCreatingBlock(false)
      return saved
    })
  }

  const showNewBlock =
    !urlState.block && isCreatingBlock && canEdit && isExplorerCurrent

  const primaryEditOp =
    canEdit && detail
      ? rowActions({ detail }).actions.find((op) => EDIT_OPS.includes(op)) ??
        null
      : null

  const content = (
    <div className="lox-blocks-explorer">
      <div className="lox-blocks-explorer__layout has-details">
        <div className="lox-blocks-explorer__side">
          {tree && (
            <TreatmentTree
              nodes={tree.nodes}
              selectedId={treatmentId}
              onSelect={selectTreatment}
            />
          )}
          {!tree && treeLoadFailed && (
            <div
              className="has-text-centered p-3"
              data-testid="treatment-tree-load-error"
            >
              <p className="has-text-danger is-size-7 mb-2">
                The treatments could not be loaded.
              </p>
              <button
                type="button"
                className="button is-small"
                onClick={retryTree}
                data-testid="treatment-tree-retry"
              >
                Retry
              </button>
            </div>
          )}
          {!tree && !treeLoadFailed && <Loading noWrapper />}
        </div>

        <div className="lox-blocks-explorer__main">
          <section
            className="lox-blocks-explorer__header"
            data-testid="blocks-header"
          >
            <div className="lox-blocks-explorer__page-head">
              <div>
                {path.length > 0 && (
                  <nav
                    className="breadcrumb is-small mb-1"
                    aria-label="breadcrumbs"
                    data-testid="blocks-breadcrumb"
                  >
                    <ul>
                      {path.map((node) => (
                        <li
                          key={node.id}
                          className={node.id === treatmentId ? 'is-active' : ''}
                        >
                          <a
                            href="#"
                            aria-current={
                              node.id === treatmentId ? 'page' : undefined
                            }
                            onClick={(event) => {
                              event.preventDefault()
                              selectTreatment(node.id)
                            }}
                          >
                            {node.name ?? `Treatment ${node.id}`}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}
                <h2 className="title is-4 mb-1" data-testid="blocks-title">
                  {currentTreatment?.name ?? 'Blocks'}
                  {currentTreatment?.is_test === 1 && (
                    <span className="lox-blocks-explorer__badge">test</span>
                  )}
                  <span className="lox-blocks-explorer__badge is-leads">
                    {urlState.customerType} leads
                  </span>
                </h2>
                {isExplorerCurrent && <StatsStrip stats={explorer.stats} />}
              </div>
              <div className="lox-blocks-explorer__page-actions">
                {urlState.changeRequestId ? null : (
                  <ParityBadge parity={parity} />
                )}
                {canEdit && (
                  <button
                    type="button"
                    className="button is-small is-primary"
                    onClick={() => {
                      setIsCreatingBlock(true)
                      updateUrl({ block: null })
                    }}
                    data-testid="blocks-new-block-open"
                  >
                    New block
                  </button>
                )}
              </div>
            </div>

            <div className="lox-blocks-explorer__controls">
              <BlocksViewModeBar
                changeRequestId={urlState.changeRequestId}
                draft={draft}
                isClosed={isDraftClosed}
                myDrafts={myDrafts}
                onSelect={selectDraft}
              />
              <BlocksToolbar
                customerType={urlState.customerType}
                onCustomerTypeChange={changeCustomerType}
                query={urlState.query}
                onQueryChange={(query) => updateUrl({ query })}
                chip={chip}
                onChipChange={setChip}
                counts={counts}
                blockTable={blockTable}
                onBlockTableChange={setBlockTable}
              />
            </div>
          </section>

          <BlocksDriftBanner checksum={checksum} />
          <BlocksDraftTray
            draft={draft}
            canEdit={canEdit}
            isBusy={isBusy}
            treatmentNamesById={treatmentNamesById}
            actionLabels={actionLabels}
            onOpen={openDraftChange}
            onRemove={removeOperation}
          />

          <div className="lox-blocks-explorer__card p-0">
            {isDraftClosed && (
              <div
                className="has-text-centered p-5"
                data-testid="blocks-draft-closed"
              >
                <p className="mb-2">
                  CR-{urlState.changeRequestId} is{' '}
                  {draft
                    ? statusMeta(
                        draft.change_request.status
                      ).label.toLowerCase()
                    : 'closed'}
                  ; it is no longer a draft.
                </p>
                <button
                  type="button"
                  className="button is-small"
                  onClick={() => selectDraft(null)}
                  data-testid="blocks-draft-closed-live"
                >
                  Back to live
                </button>
              </div>
            )}
            {!isDraftClosed && explorerLoadFailed && (
              <div
                className="has-text-centered p-5"
                data-testid="blocks-load-error"
              >
                <p className="has-text-danger mb-2">
                  The blocks for this view could not be loaded.
                </p>
                <button
                  type="button"
                  className="button is-small"
                  onClick={retryExplorer}
                  data-testid="blocks-load-retry"
                >
                  Retry
                </button>
              </div>
            )}
            {!isDraftClosed &&
              !explorerLoadFailed &&
              (isExplorerLoading || !isExplorerCurrent) && <Loading />}
            {!isDraftClosed &&
              !explorerLoadFailed &&
              !isExplorerLoading &&
              isExplorerCurrent && (
                <>
                  <BlocksTable
                    rows={paged.pageRows}
                    sort={sort}
                    onSort={toggleSort}
                    actionLabels={actionLabels}
                    treatmentNamesById={treatmentNamesById}
                    treatmentId={treatmentId}
                    selectedBlock={urlState.block}
                    onSelect={(block) => {
                      setIsCreatingBlock(false)
                      updateUrl({ block })
                    }}
                  />
                  {rows.length > 0 && (
                    <BlocksPager
                      total={rows.length}
                      page={paged.page}
                      totalPages={paged.totalPages}
                      perPage={perPage}
                      onPageChange={setPage}
                      onPerPageChange={(size) => {
                        setPerPage(size)
                        setPage(1)
                      }}
                    />
                  )}
                </>
              )}
          </div>
          {isExplorerCurrent &&
            explorer.dropped_by_customer_type.length > 0 && (
              <p
                className="is-size-7 has-text-grey mt-2"
                data-testid="blocks-dropped-note"
              >
                {explorer.dropped_by_customer_type.length} inherited block(s)
                are cut by the {urlState.customerType} filter here:{' '}
                {explorer.dropped_by_customer_type
                  .map((dropped) => `#${dropped.block.row.id}`)
                  .join(', ')}
              </p>
            )}
        </div>

        {urlState.block && !isDraftClosed && (
          <div className="lox-blocks-explorer__details">
            <BlockDrawer
              key={`${urlState.block.table}:${urlState.block.id}`}
              detail={detail}
              isLoading={isDetailLoading}
              loadFailed={detailLoadFailed}
              onRetry={retryDetail}
              actionLabels={actionLabels}
              onClose={() => updateUrl({ block: null })}
              tab={drawerTab}
              onTabChange={setDrawerTab}
              onCopyRule={copyRule}
              onEditRule={
                primaryEditOp ? () => setEditingOp(primaryEditOp) : null
              }
              footer={
                canEdit && detail ? (
                  <BlockChangeActions
                    key={`${detail.table}:${detail.row.id}`}
                    detail={detail}
                    changeRequestId={urlState.changeRequestId}
                    treatmentId={treatmentId}
                    vocabulary={vocabulary}
                    onRetryVocabulary={
                      vocabularyLoadFailed ? retryVocabulary : null
                    }
                    isBusy={isBusy}
                    editingOp={editingOp}
                    onEditingOpChange={setEditingOp}
                    onSubmit={submitBlockOperation}
                    draftChanges={cardsForBlock({
                      cards: draft?.diff_cards ?? [],
                      table: detail.table,
                      id: detail.row.id,
                    })}
                    treatmentNamesById={treatmentNamesById}
                    actionLabels={actionLabels}
                  />
                ) : null
              }
            />
          </div>
        )}
        {!urlState.block && !showNewBlock && (
          <div
            className="lox-blocks-explorer__details"
            data-testid="block-details-placeholder"
          >
            <div className="lox-blocks-explorer__card has-text-grey is-size-7">
              {isExplorerCurrent && (
                <p>
                  {rows.length === 0
                    ? 'No blocks to show for this treatment.'
                    : 'Select a block to see its details.'}
                </p>
              )}
              {!isExplorerCurrent && explorerLoadFailed && (
                <p>Blocks could not be loaded for this view.</p>
              )}
              {!isExplorerCurrent && !explorerLoadFailed && (
                <Loading noWrapper />
              )}
            </div>
          </div>
        )}
        {showNewBlock && (
          <div className="lox-blocks-explorer__details">
            <BlocksNewBlockPanel
              changeRequestId={urlState.changeRequestId}
              treatmentId={explorer.lineage[explorer.lineage.length - 1]}
              vocabulary={vocabulary}
              onRetryVocabulary={vocabularyLoadFailed ? retryVocabulary : null}
              isBusy={isBusy}
              onSubmit={createBlock}
              onClose={() => setIsCreatingBlock(false)}
            />
          </div>
        )}
      </div>
    </div>
  )

  return <LayoutSidebar contentTitle="Blocks Explorer" content={content} />
}
